class AnimatedAccordion extends HTMLElement {
  constructor() {
    super();
    this.boundClick = this.handleClick.bind(this);
    this.triggers = [];
  }

  connectedCallback() {
    this.allowMultiple = this.dataset.allowMultiple === 'true';
    this.bindTriggers();
  }

  disconnectedCallback() {
    this.unbindTriggers();
  }

  bindTriggers() {
    this.unbindTriggers();
    this.triggers = Array.from(this.querySelectorAll('[data-accordion-trigger]'));
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.boundClick);
    });
  }

  unbindTriggers() {
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.boundClick);
    });
    this.triggers = [];
  }

  handleClick(event) {
    const trigger = event.currentTarget;
    const item = trigger.closest('[data-accordion-item]');
    if (!item || !this.contains(item)) return;

    const isOpen = item.classList.contains('is-open');

    if (!this.allowMultiple) {
      this.querySelectorAll('[data-accordion-item]').forEach((accordionItem) => {
        if (accordionItem !== item) this.closeItem(accordionItem);
      });
    }

    if (isOpen) {
      this.closeItem(item);
    } else {
      this.openItem(item);
    }
  }

  openItem(item) {
    const trigger = item.querySelector('[data-accordion-trigger]');
    if (!trigger) return;

    item.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');
  }

  closeItem(item) {
    const trigger = item.querySelector('[data-accordion-trigger]');
    if (!trigger) return;

    item.classList.remove('is-open');
    trigger.setAttribute('aria-expanded', 'false');
  }
}

if (!customElements.get('animated-accordion')) {
  customElements.define('animated-accordion', AnimatedAccordion);
}

class StickyHeader extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.header = document.querySelector('.section-header');
      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};

      this.megaMenuItems = this.querySelectorAll('.mega-menu__wrapper');
      this.megaMenuTogglers = this.querySelectorAll('.header__menu-item--mega-menu');
      this.megaMenu = this.querySelector('.mega-menu');

      this.setHeaderHeight();

      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);

      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, false);

      this.updateScrolledClass(window.pageYOffset || document.documentElement.scrollTop);

      this.createObserver();


      if (window.innerWidth > 990) {
        this.megaMenuTogglers.forEach(toggler => {
          toggler.addEventListener('mouseenter', () => this.openMegaMenu(toggler.dataset.itemPosition, toggler));
        });
        this.megaMenu.addEventListener('mouseleave', () => this.closeMegaMenu());
        this.addEventListener('mouseleave', () => this.closeMegaMenu());
      }
    }

    openMegaMenu(position, toggler){
        this.megaMenu.classList.remove('hidden');
        this.megaMenuTogglers.forEach(item => item.classList.remove('active'));
        toggler.classList.add('active');
        console.log(position);
        console.log(this.megaMenuItems);
        const activeItem = Array.from(this.megaMenuItems).find(item => item.dataset.position === position);
        this.megaMenuItems.forEach(item => item.classList.remove('active'));
        activeItem.classList.add('active');
    }

    closeMegaMenu(){
        this.megaMenuTogglers.forEach(toggler => {
            toggler.classList.remove('active');
        });
        this.megaMenuItems.forEach(item => item.classList.remove('active'));
        this.megaMenu.classList.add('hidden');
    }

    setHeaderHeight() {
      const announcementBarHeight = document.querySelector('.utility-bar')?.offsetHeight || 0;
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight + announcementBarHeight}px`);
      document.documentElement.style.setProperty('--header-height-no-announcement', `${this.header.offsetHeight}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });

      observer.observe(this.header);
    }

    updateScrolledClass(scrollTop) {
      this.classList.toggle('scrolled', scrollTop > 20);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      this.updateScrolledClass(scrollTop);

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);

          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);

          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => disclosure.close());
    }

    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      this.searchModal.close(false);
    }
  }

  customElements.define('sticky-header', StickyHeader);



  class MegaMenu extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.mobileToggler = document.querySelector('.js-toggle-menu');
      this.megaMenuTogglers = this.querySelectorAll('.mega-menu__toggler');
      this.megaMenuItems = this.querySelectorAll('.mega-menu__wrapper');
      this.accountToggler = document.querySelector('.header__icon--account');
      this.opened = false;

      this.mobileToggler?.addEventListener('click', () => {
        this.opened ? this.closeMenu() : this.openMenu();
      });

      this.megaMenuTogglers.forEach(toggler => {
        toggler.addEventListener('click', () => {
          this.selectItem(toggler.dataset.position);
          this.megaMenuTogglers.forEach(item => item.classList.remove('active'));
          toggler.classList.add('active');
        });
      });

      this.accountToggler?.addEventListener('click', (event) => {
        if (!this.accountToggler.getAttribute('href')?.startsWith('javascript:')) return;
        event.preventDefault();
        this.openMenu('-1');
      });
    }

    selectItem(position) {
      this.megaMenuItems.forEach(item => item.classList.remove('active'));
      const activeItem = Array.from(this.megaMenuItems).find(item => item.dataset.position === String(position));
      activeItem?.classList.add('active');
    }

    openMenu(position) {
      this.classList.remove('hidden');
      this.mobileToggler?.classList.add('active');
      this.megaMenuTogglers.forEach(toggler => toggler.classList.remove('active'));

      if (position != null) {
        this.selectItem(position);
      } else {
        this.megaMenuTogglers[0]?.classList.add('active');
        this.megaMenuItems[0]?.classList.add('active');
      }

      document.querySelector('.header-wrapper').style.background = '#fff';
      this.opened = true;
    }

    closeMenu() {
      this.classList.add('hidden');
      this.mobileToggler?.classList.remove('active');
      this.megaMenuTogglers.forEach(toggler => toggler.classList.remove('active'));
      this.megaMenuItems.forEach(item => item.classList.remove('active'));
      document.querySelector('.header-wrapper').removeAttribute('style');
      this.opened = false;
    }
  }

  customElements.define('mega-menu', MegaMenu);

class TestimonialsSlider extends HTMLElement {
  constructor() {
    super();
    this.boundPlayClick = this.handlePlayClick.bind(this);
    this.boundCardClick = this.handleCardClick.bind(this);
    this.boundVideoPause = this.handleVideoPause.bind(this);
    this.boundResize = this.handleResize.bind(this);
    this.boundLayoutChange = this.applyLayout.bind(this);
  }

  connectedCallback() {
    this.videos = Array.from(this.querySelectorAll('video.testimonials__video'));
    this.playButtons = Array.from(this.querySelectorAll('.media-video-play'));
    this.cards = Array.from(this.querySelectorAll('.testimonials__card'));
    this.mobileStack = this.dataset.mobileStack === 'true';
    this.desktopQuery = window.matchMedia('(min-width: 990px)');

    this.applyLayout();
    this.updatePosters();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.destroySwiper();

    if (this.desktopQuery) {
      this.desktopQuery.removeEventListener('change', this.boundLayoutChange);
    }
  }

  shouldUseSwiper() {
    return this.desktopQuery.matches || !this.mobileStack;
  }

  applyLayout() {
    if (this.shouldUseSwiper()) {
      this.classList.remove('is-static-mobile');
      if (!this.swiper) this.createSwiper();
      else this.swiper.update();
    } else {
      this.destroySwiper();
      this.classList.add('is-static-mobile');
    }
  }

  destroySwiper() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
  }

  createSwiper() {
    const swiperElement = this.querySelector('.swiper');
    if (!swiperElement) return;

    const slidesMobile = Number(this.dataset.slidesMobile) || 1.3;
    const slidesDesktop = Number(this.dataset.slidesDesktop) || 4;
    const spaceMobile = Number(this.dataset.spaceMobile) || 8;
    const spaceDesktop = Number(this.dataset.spaceDesktop) || 12;
    const isDesktop = this.desktopQuery.matches;

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: isDesktop ? 'auto' : slidesMobile,
      spaceBetween: isDesktop ? spaceDesktop : spaceMobile,
      breakpoints: {
        990: {
          slidesPerView: 'auto',
          spaceBetween: spaceDesktop
        }
      }
    });
  }

  handleResize() {
    this.updatePosters();
    this.applyLayout();
  }

  bindEvents() {
    this.playButtons.forEach((button) => {
      button.addEventListener('click', this.boundPlayClick);
    });

    this.cards.forEach((card) => {
      card.addEventListener('click', this.boundCardClick);
    });

    this.videos.forEach((video) => {
      video.addEventListener('pause', this.boundVideoPause);
      video.addEventListener('ended', this.boundVideoPause);
    });

    window.addEventListener('resize', this.boundResize);
    this.desktopQuery.addEventListener('change', this.boundLayoutChange);
    // Intersection Observer for autoplay videos
    if ('IntersectionObserver' in window) {
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          if (!video.hasAttribute('data-autoplay')) return;

          if (entry.isIntersecting) {
            video.muted = true;
            video.loop = true;
            if (video.paused) {
              video.play();
            }
          } else {
            if (!video.paused) {
              video.pause();
            }
          }
        });
      }, {
        threshold: 0.5
      });

      this.videos.forEach((video) => {
        if (video.hasAttribute('data-autoplay')) {
          this.videoObserver.observe(video);
        }
      });
    }
  }

  unbindEvents() {
    this.playButtons.forEach((button) => {
      button.removeEventListener('click', this.boundPlayClick);
    });

    this.cards.forEach((card) => {
      card.removeEventListener('click', this.boundCardClick);
    });

    this.videos.forEach((video) => {
      video.removeEventListener('pause', this.boundVideoPause);
      video.removeEventListener('ended', this.boundVideoPause);
    });

    window.removeEventListener('resize', this.boundResize);
  }

  handlePlayClick(event) {
    event.stopPropagation();
    const button = event.currentTarget;
    const card = button.closest('.testimonials__card');
    const targetVideo = card ? card.querySelector('video.testimonials__video') : null;
    if (!targetVideo) return;

    this.toggleVideo(targetVideo, card);
  }

  handleCardClick(event) {
    if (event.target.closest('.media-video-play')) return;

    const card = event.currentTarget;
    const targetVideo = card.querySelector('video.testimonials__video');
    if (!targetVideo) return;

    this.toggleVideo(targetVideo, card);
  }

  toggleVideo(targetVideo, card) {
    if (targetVideo.tagName !== 'VIDEO') return;

    // Autoplay videos start muted; the first user play should restart from the start unmuted
    // rather than treating the muted autoplay as an active (pausable) playback.
    const isAutoplayPreview = targetVideo.hasAttribute('autoplay') && targetVideo.muted;

    if (!targetVideo.paused && !targetVideo.ended && !isAutoplayPreview) {
      targetVideo.pause();
      return;
    }

    this.videos.forEach((video) => {
      if (video === targetVideo) return;
      // Leave muted autoplay previews running; only stop other user-activated playback
      if (video.hasAttribute('autoplay') && video.muted) return;

      const parentCard = video.closest('.testimonials__card');
      video.pause();
      video.controls = false;
      if (parentCard) parentCard.classList.remove('is-playing');
    });

    if (isAutoplayPreview) {
      targetVideo.currentTime = 0;
    }

    targetVideo.muted = false;
    targetVideo.play().then(() => {
      if (card) card.classList.add('is-playing');
    }).catch(() => {});
  }

  handleVideoPause(event) {
    const video = event.currentTarget;
    const card = video.closest('.testimonials__card');
    if (!video.paused || !card) return;

    video.controls = false;
    card.classList.remove('is-playing');
  }

  updatePosters() {
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const cards = Array.from(this.querySelectorAll('.testimonials__card'));

    cards.forEach((card) => {
      const video = card.querySelector('video.testimonials__video');
      if (!video) return;

      const mobilePoster = card.dataset.posterMobile;
      const desktopPoster = card.dataset.posterDesktop;
      const selectedPoster = isMobile ? (mobilePoster || desktopPoster) : (desktopPoster || mobilePoster);

      if (selectedPoster) {
        video.poster = selectedPoster;
      }
    });
  }
}

customElements.define('testimonials-slider', TestimonialsSlider);

class BeforeAfterSlider extends HTMLElement {
  constructor() {
    super();
    this.isDragging = false;
    this.boundPointerDown = this.handlePointerDown.bind(this);
    this.boundPointerMove = this.handlePointerMove.bind(this);
    this.boundPointerUp = this.handlePointerUp.bind(this);
  }

  connectedCallback() {
    this.canvas = this.querySelector('.before-after__canvas');
    this.handle = this.querySelector('.before-after__handle');
    if (!this.canvas || !this.handle) return;

    this.setPosition(Number(this.dataset.startPosition) || 50);

    this.handle.addEventListener('pointerdown', this.boundPointerDown);
    this.canvas.addEventListener('pointerdown', this.boundPointerDown);
    window.addEventListener('pointermove', this.boundPointerMove);
    window.addEventListener('pointerup', this.boundPointerUp);
    window.addEventListener('pointercancel', this.boundPointerUp);
  }

  disconnectedCallback() {
    if (this.handle) {
      this.handle.removeEventListener('pointerdown', this.boundPointerDown);
    }
    if (this.canvas) {
      this.canvas.removeEventListener('pointerdown', this.boundPointerDown);
    }
    window.removeEventListener('pointermove', this.boundPointerMove);
    window.removeEventListener('pointerup', this.boundPointerUp);
    window.removeEventListener('pointercancel', this.boundPointerUp);
  }

  handlePointerDown(event) {
    if (!this.canvas) return;
    this.isDragging = true;
    this.canvas.setPointerCapture(event.pointerId);
    this.updateFromEvent(event);
  }

  handlePointerMove(event) {
    if (!this.isDragging) return;
    this.updateFromEvent(event);
  }

  handlePointerUp() {
    this.isDragging = false;
  }

  updateFromEvent(event) {
    if (!this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    if (!rect.width) return;

    const rawPosition = ((event.clientX - rect.left) / rect.width) * 100;
    this.setPosition(rawPosition);
  }

  setPosition(value) {
    const clamped = Math.max(0, Math.min(100, value));
    this.style.setProperty('--comparison-position', `${clamped}%`);
  }
}

if (!customElements.get('before-after-slider')) {
  customElements.define('before-after-slider', BeforeAfterSlider);
}


class DoctorTestimonials extends HTMLElement {
  constructor() {
    super();
    this.boundVideoClick = this.handleVideoClick.bind(this);
    this.boundVideoPlay = this.handleVideoPlay.bind(this);
    this.boundVideoPause = this.handleVideoPause.bind(this);
  }

  connectedCallback() {
    this.videos = Array.from(this.querySelectorAll('.doctor-testimonials__video'));
    this.initSwiper();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  parseSlidesPerView(value, fallback = 1) {
    if (value === 'auto') return 'auto';
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  initSwiper() {
    const swiperElement = this;
    if (!swiperElement) return;

    const slidesMobile = this.parseSlidesPerView(this.dataset.slidesMobile, 1);
    const slidesDesktop = this.parseSlidesPerView(this.dataset.slidesDesktop, slidesMobile);
    const spaceBetween = Number(this.dataset.spaceBetween) || 12;
    const nextEl = this.querySelector('.doctor-testimonials__next');
    const prevEl = this.querySelector('.doctor-testimonials__prev');

    const options = {
      slidesPerView: slidesMobile,
      spaceBetween,
      observer: true,
      observeParents: true,
      breakpoints: {
        990: {
          slidesPerView: slidesDesktop,
        },
      },
      on: {
        slideChangeTransitionStart: () => {
          this.pauseAllVideos();
        },
      },
    };

    if (nextEl && prevEl) {
      options.navigation = { nextEl, prevEl };
    }

    this.swiper = new Swiper(swiperElement, options);
  }

  bindEvents() {
    this.videos.forEach((video) => {
      video.addEventListener('click', this.boundVideoClick);
      video.addEventListener('play', this.boundVideoPlay);
      video.addEventListener('pause', this.boundVideoPause);
      video.addEventListener('ended', this.boundVideoPause);
    });
  }

  unbindEvents() {
    this.videos.forEach((video) => {
      video.removeEventListener('click', this.boundVideoClick);
      video.removeEventListener('play', this.boundVideoPlay);
      video.removeEventListener('pause', this.boundVideoPause);
      video.removeEventListener('ended', this.boundVideoPause);
    });
  }

  handleVideoClick(event) {
    const targetVideo = event.currentTarget;
    if (!targetVideo) return;

    this.toggleVideo(targetVideo);
  }

  toggleVideo(targetVideo) {
    if (!targetVideo.paused && !targetVideo.ended) {
      targetVideo.pause();
      return;
    }

    this.pauseAllVideos(targetVideo);

    targetVideo.muted = false;
    targetVideo.play().catch(() => {});
  }

  pauseAllVideos(excludedVideo = null) {
    this.videos.forEach((video) => {
      if (video !== excludedVideo) {
        video.pause();
      }
    });
  }

  handleVideoPlay(event) {
    const video = event.currentTarget;
    if (!video) return;
    this.updateVideoState(video, true);
  }

  handleVideoPause(event) {
    const video = event.currentTarget;
    if (!video || !video.paused) return;
    this.updateVideoState(video, false);
  }

  updateVideoState(video, isPlaying) {
    const container = video.closest('.doctor-testimonials__video-container');
    if (!container) return;

    container.classList.toggle('is-playing', isPlaying);
  }
}

customElements.define('doctor-testimonials', DoctorTestimonials);

class CardsBanner extends HTMLElement {
  constructor() {
    super();
    this.boundViewportChange = this.handleViewportChange.bind(this);
    this.boundResize = this.updateMinHeight.bind(this);
    this.boundLoad = this.updateMinHeight.bind(this);
  }

  connectedCallback() {
    this.mobileBreakpoint = window.matchMedia('(max-width: 989px)');
    this.mobileBreakpoint.addEventListener('change', this.boundViewportChange);
    window.addEventListener('resize', this.boundResize);
    window.addEventListener('load', this.boundLoad);

    this.handleViewportChange();
    this.updateMinHeight();
    setTimeout(() => this.updateMinHeight(), 120);
  }

  disconnectedCallback() {
    if (this.mobileBreakpoint) {
      this.mobileBreakpoint.removeEventListener('change', this.boundViewportChange);
    }
    window.removeEventListener('resize', this.boundResize);
    window.removeEventListener('load', this.boundLoad);

    this.destroySlider();
  }

  handleViewportChange() {
    if (!this.mobileBreakpoint) return;

    if (this.mobileBreakpoint.matches) {
      this.initSlider();
      this.updateMinHeight();
      return;
    }

    this.destroySlider();
    this.updateMinHeight();
  }

  initSlider() {
    if (this.swiper) return;

    const slider = this.querySelector('.cards-banner__cards-mobile .swiper');
    if (!slider) return;

    const isInline = this.dataset.sectionType === 'inline';

    this.swiper = new Swiper(slider, isInline ? 
      {
        slidesPerView: 1.7,
        spaceBetween: 10,
        centeredSlides: false,
        slidesPerGroupAuto: false,
        autoHeight: true,
      } 
      :
      {
        slidesPerView: 1.8,
        spaceBetween: 10,
        slidesPerGroupAuto: false,
        autoHeight: true,
      }
    );
  }

  destroySlider() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
  }

  updateMinHeight() {
    const inner = this.querySelector('.cards-banner__inner');
    if (!inner) return;

    if (this.dataset.sectionType === 'inline' && !this.mobileBreakpoint?.matches) {
      inner.style.setProperty('--cards-banner-min-height', '0px');
      return;
    }

    const desktopCards = Array.from(this.querySelectorAll('.cards-banner__cards-desktop .cards-banner__card'));
    if (!desktopCards.length) {
      inner.style.setProperty('--cards-banner-min-height', '0px');
      return;
    }

    let requiredHeight = 0;

    desktopCards.forEach((card) => {
      const posY = Number(card.dataset.posY);
      const cardHeight = card.offsetHeight;
      if (!cardHeight || Number.isNaN(posY)) return;

      const normalizedY = Math.max(1, Math.min(99, posY)) / 100;
      const minForTop = cardHeight / (2 * normalizedY);
      const minForBottom = cardHeight / (2 * (1 - normalizedY));
      requiredHeight = Math.max(requiredHeight, minForTop, minForBottom);
    });

    inner.style.setProperty('--cards-banner-min-height', `${Math.ceil(requiredHeight)}px`);
  }
}

if (!customElements.get('cards-banner')) {
  customElements.define('cards-banner', CardsBanner);
}

class TimelineBanner extends HTMLElement {
  constructor() {
    super();
    this.boundClick = this.handleClick.bind(this);
    this.boundScroll = this.handleScroll.bind(this);
    this.activeIndex = 0;
    this.scrollRaf = null;
  }

  connectedCallback() {
    this.scrollTrack = this.querySelector('.timeline-banner__cards-scroll');
    this.addEventListener('click', this.boundClick);
    this.scrollTrack?.addEventListener('scroll', this.boundScroll, { passive: true });
    this.handleScroll();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundClick);
    this.scrollTrack?.removeEventListener('scroll', this.boundScroll);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  }

  handleClick(event) {
    const trigger = event.target.closest('[data-index]');
    if (!trigger || !this.contains(trigger)) return;

    const index = Number(trigger.dataset.index);
    if (Number.isNaN(index)) return;

    this.setActive(index);

    const slide = this.querySelector(`.timeline-banner__card-slide[data-index="${index}"]`);
    if (slide && this.scrollTrack) {
      const offset = slide.offsetLeft - (this.scrollTrack.clientWidth - slide.offsetWidth) / 2;
      this.scrollTrack.scrollTo({ left: offset, behavior: 'smooth' });
    }
  }

  handleScroll() {
    if (!this.scrollTrack) return;

    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    this.scrollRaf = requestAnimationFrame(() => {
      const slides = Array.from(this.querySelectorAll('.timeline-banner__card-slide'));
      if (!slides.length) return;

      const trackCenter = this.scrollTrack.scrollLeft + this.scrollTrack.clientWidth / 2;
      let closestIndex = 0;
      let minDistance = Infinity;

      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(trackCenter - slideCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      this.setActive(closestIndex);
    });
  }

  setActive(index) {
    this.activeIndex = index;

    this.querySelectorAll('.timeline-banner__marker, .timeline-banner__card[data-index], .timeline-banner__card-slide').forEach((item) => {
      const itemIndex = Number(item.dataset.index);
      const isActive = itemIndex === index;

      item.classList.toggle('is-active', isActive);

      if (item.matches('button[data-index]')) {
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
    });
  }
}

if (!customElements.get('timeline-banner')) {
  customElements.define('timeline-banner', TimelineBanner);
}

class ProductGalleryVideos extends HTMLElement {
  constructor() {
    super();
    this.boundClick = this.handleClick.bind(this);
    this.boundVideoPause = this.handleVideoPause.bind(this);
    this.boundSlideChanged = this.handleSlideChanged.bind(this);
    this.enableAutoplayVideos = this.hasAttribute('data-enable-autoplay-videos');
    this.readyPromises = new WeakMap();
  }

  connectedCallback() {
    this.galleryViewer = this.closest('media-gallery')?.querySelector('[id^="GalleryViewer"]');
    this.addEventListener('click', this.boundClick);
    this.addEventListener('pause', this.boundVideoPause, true);
    this.addEventListener('ended', this.boundVideoPause, true);
    this.galleryViewer?.addEventListener('slideChanged', this.boundSlideChanged);
    this.prepareVideos();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundClick);
    this.removeEventListener('pause', this.boundVideoPause, true);
    this.removeEventListener('ended', this.boundVideoPause, true);
    this.galleryViewer?.removeEventListener('slideChanged', this.boundSlideChanged);
  }

  isAutoplayVideo(video) {
    return video.hasAttribute('data-autoplay') || (this.enableAutoplayVideos && video.muted);
  }

  prepareVideos() {
    this.querySelectorAll('.product__media-video').forEach((video) => {
      if (!this.isAutoplayVideo(video)) return;

      this.waitUntilReady(video).then(() => {
        if (!this.isConnected || !this.contains(video)) return;
        this.revealVideo(video);
        video.muted = true;
        video.play().catch(() => {});
      });
    });
  }

  revealVideo(video) {
    const card = video.closest('[data-product-video-card]');
    video.dataset.ready = 'true';
    card?.classList.add('is-ready');
  }

  waitUntilReady(video) {
    if (this.readyPromises.has(video)) return this.readyPromises.get(video);

    const promise = new Promise((resolve) => {
      let settled = false;
      let fallbackTimer;

      const onCanPlayThrough = () => finish();
      const onProgress = () => {
        // Safari sometimes never fires canplaythrough; fall back once enough is buffered
        if (video.readyState < 3 || !video.buffered.length) return;
        const bufferedEnd = video.buffered.end(video.buffered.length - 1);
        const duration = video.duration || 0;
        if (duration && bufferedEnd / duration >= 0.5) finish();
      };

      const finish = () => {
        if (settled) return;
        settled = true;
        video.removeEventListener('canplaythrough', onCanPlayThrough);
        video.removeEventListener('progress', onProgress);
        window.clearTimeout(fallbackTimer);
        resolve(video);
      };

      // HAVE_ENOUGH_DATA — browser can play through at current quality without stalling
      if (video.readyState >= 4) {
        finish();
        return;
      }

      video.addEventListener('canplaythrough', onCanPlayThrough);
      video.addEventListener('progress', onProgress);

      // Hard fallback so poster never sticks forever on flaky networks
      fallbackTimer = window.setTimeout(finish, 12000);
    });

    this.readyPromises.set(video, promise);
    return promise;
  }

  handleClick(event) {
    if (this.enableAutoplayVideos) return;

    const playButton = event.target.closest('.media-video-play');
    if (playButton && this.contains(playButton)) {
      event.stopPropagation();
      const card = playButton.closest('[data-product-video-card]');
      const targetVideo = card?.querySelector('.product__media-video');
      if (targetVideo) this.toggleVideo(targetVideo, card);
      return;
    }

    const card = event.target.closest('[data-product-video-card]');
    if (!card || !this.contains(card)) return;

    const targetVideo = card.querySelector('.product__media-video');
    if (targetVideo) this.toggleVideo(targetVideo, card);
  }

  handleSlideChanged() {
    this.pauseAllVideos();
  }

  toggleVideo(targetVideo, card) {
    if (this.enableAutoplayVideos || this.isAutoplayVideo(targetVideo)) return;
    if (!targetVideo.paused && !targetVideo.ended) {
      targetVideo.pause();
      return;
    }

    this.querySelectorAll('.product__media-video').forEach((video) => {
      if (video === targetVideo) return;
      if (this.isAutoplayVideo(video)) return;

      const parentCard = video.closest('[data-product-video-card]');
      video.pause();
      video.controls = false;
      if (parentCard) parentCard.classList.remove('is-playing');
    });

    this.waitUntilReady(targetVideo).then(() => {
      if (!this.isConnected || !this.contains(targetVideo)) return;
      this.revealVideo(targetVideo);
      targetVideo.muted = false;
      targetVideo.play().then(() => {
        if (card) card.classList.add('is-playing');
      }).catch(() => {});
    });
  }

  handleVideoPause(event) {
    const video = event.target;
    if (!video.matches('.product__media-video') || !this.contains(video)) return;

    // If something else paused a muted autoplay preview, keep it running
    if (this.isAutoplayVideo(video) && video.muted && event.type === 'pause') {
      if (video.dataset.ready === 'true') video.play().catch(() => {});
      return;
    }

    const card = video.closest('[data-product-video-card]');
    if (!video.paused || !card) return;

    video.controls = false;
    card.classList.remove('is-playing');
  }

  pauseAllVideos() {
    this.querySelectorAll('.product__media-video').forEach((video) => {
      if (this.isAutoplayVideo(video)) {
        if (video.dataset.ready === 'true') video.play().catch(() => {});
        return;
      }

      const parentCard = video.closest('[data-product-video-card]');
      video.pause();
      video.controls = false;
      if (parentCard) parentCard.classList.remove('is-playing');
    });
  }
}

if (!customElements.get('product-gallery-videos')) {
  customElements.define('product-gallery-videos', ProductGalleryVideos);
}

class PurchaseOptionsDropdown extends HTMLElement {
  constructor() {
    super();
    this.boundTriggerClick = this.handleTriggerClick.bind(this);
    this.boundDocumentClick = this.handleDocumentClick.bind(this);
    this.boundOptionClick = this.handleOptionClick.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-dropdown-trigger]');
    this.panel = this.querySelector('[data-dropdown-panel]');
    if (!this.trigger || !this.panel) return;

    this.trigger.addEventListener('click', this.boundTriggerClick);
    document.addEventListener('click', this.boundDocumentClick);
    this.querySelectorAll('purchase-option').forEach((option) => {
      option.addEventListener('click', this.boundOptionClick);
    });
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.boundTriggerClick);
    document.removeEventListener('click', this.boundDocumentClick);
    this.querySelectorAll('purchase-option').forEach((option) => {
      option.removeEventListener('click', this.boundOptionClick);
    });
  }

  handleTriggerClick(event) {
    event.stopPropagation();
    this.toggle();
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  handleOptionClick(event) {
    const title = event.currentTarget.querySelector('.purchase-option__title')?.textContent?.trim();
    const label = this.querySelector('.purchase-options-dropdown__trigger-label');
    if (label && title) label.textContent = title;
    this.close();
  }

  toggle() {
    if (this.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.hidden = false;
  }

  close() {
    this.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
    this.panel.hidden = true;
  }
}

if (!customElements.get('purchase-options-dropdown')) {
  customElements.define('purchase-options-dropdown', PurchaseOptionsDropdown);
}


class PurchaseOption extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.url = this.dataset.url;
  }

  connectedCallback() {
    this.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(event) {
    if (this.classList.contains('active')) return;

    const product = this.closest('.product');
    const scrollY = document.documentElement.scrollTop || window.scrollY;
    const url = this.url;
    document.documentElement.classList.add('is-updating-purchase-option');

    let unlocked = false;
    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      document.documentElement.scrollTop = scrollY;
      document.documentElement.classList.remove('is-updating-purchase-option');
      document.querySelector('sticky-atc')?.syncVisibility();
    };

    fetch(`${url}?section_id=${this.sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const productHtml = html.querySelector('.product');
        product.innerHTML = productHtml.innerHTML;
        document.documentElement.scrollTop = scrollY;
        window.history.pushState(null, '', url);

        requestAnimationFrame(() => {
          document.documentElement.scrollTop = scrollY;
          requestAnimationFrame(unlock);
        });
        setTimeout(unlock, 50);
      })
      .catch(() => unlock());
  }
}

customElements.define('purchase-option', PurchaseOption);



class StickyATC extends HTMLElement {
  constructor() {
    super();
    this.mainButtonVisible = true;
    this.footerVisible = false;
  }

  connectedCallback() {
    this.mainButton = document.querySelector('.product__info-wrapper .product-form__submit');
    this.footer = document.querySelector('.footer');

    if (this.mainButton) {
      this.mainButtonObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry || entry.boundingClientRect.height === 0) return;
        this.mainButtonVisible = entry.isIntersecting;
        this.syncVisibility();
      });
      this.mainButtonObserver.observe(this.mainButton);
    }

    if (this.footer) {
      this.footerObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (!entry || entry.boundingClientRect.height === 0) return;
        this.footerVisible = entry.isIntersecting;
        this.syncVisibility();
      });
      this.footerObserver.observe(this.footer);
    }
  }

  syncVisibility() {
    if (document.documentElement.classList.contains('is-updating-purchase-option')) {
      this.classList.remove('is-visible');
      return;
    }

    const shouldShow = !this.mainButtonVisible && !this.footerVisible;
    this.classList.toggle('is-visible', shouldShow);
    this.classList.add('loaded');

    if (!this.classList.contains('is-ready')) {
      requestAnimationFrame(() => this.classList.add('is-ready'));
    }
  }

  disconnectedCallback() {
    this.mainButtonObserver?.disconnect();
    this.footerObserver?.disconnect();
  }
}

customElements.define('sticky-atc', StickyATC);


class CartDiscount extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input');
    this.submitButton = this.querySelector('button');

    this.input.addEventListener('input', this.handleInput.bind(this));
    this.submitButton.addEventListener('click', this.handleSubmit.bind(this));
  }

  handleInput(event) {
    this.submitButton.disabled = !this.input.value.trim();
  }

  handleSubmit(event) {
    event.preventDefault();
    console.log(this.input.value);
    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        discount: this.input.value,
        sections: ['cart-drawer'],
      })
    })
    .then(response => {
      return response.json();
    })
    .then((response) => {
      document.querySelector('cart-drawer').renderContents(response);
    })
    .catch((error) => {
      console.error('Error:', error);
    });
  }
}

customElements.define('cart-discount', CartDiscount);

class CartUpsellsSlider extends HTMLElement {
  connectedCallback() {
    const slider = this.querySelector('.swiper');
    if (!slider || this.swiper) return;

    const slidesMobile = Number(this.dataset.slidesMobile) || 1.25;
    const slidesDesktop = Number(this.dataset.slidesDesktop) || 1.25;
    const spaceMobile = Number(this.dataset.spaceMobile) || 16;
    const spaceDesktop = Number(this.dataset.spaceDesktop) || 20;

    this.swiper = new Swiper(slider, {
      slidesPerView: slidesMobile,
      spaceBetween: spaceMobile,
      breakpoints: {
        990: {
          slidesPerView: slidesDesktop,
          spaceBetween: spaceDesktop,
        },
      },
    });
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

if (!customElements.get('cart-upsells-slider')) {
  customElements.define('cart-upsells-slider', CartUpsellsSlider);
}

class VideoCard extends HTMLElement {
  static plyrLoadPromise = null;

  constructor() {
    super();
    this.boundTriggerClick = this.handleTriggerClick.bind(this);
    this.boundClose = this.closeModal.bind(this);
    this.boundKeyup = this.handleKeyup.bind(this);
    this.plyr = null;
    this.lastOpener = null;
  }

  connectedCallback() {
    this.modal = this.querySelector('.video-cards__modal');
    this.playerRoot = this.querySelector('[data-video-player]');
    this.dialog = this.querySelector('.video-cards__modal-dialog');
    this.trigger = this.querySelector('[data-video-trigger]');

    this.trigger?.addEventListener('click', this.boundTriggerClick);

    this.querySelectorAll('[data-modal-close]').forEach((closeBtn) => {
      closeBtn.addEventListener('click', this.boundClose);
    });
  }

  disconnectedCallback() {
    this.closeModal();

    this.trigger?.removeEventListener('click', this.boundTriggerClick);
    this.querySelectorAll('[data-modal-close]').forEach((closeBtn) => {
      closeBtn.removeEventListener('click', this.boundClose);
    });
    document.removeEventListener('keyup', this.boundKeyup);
  }

  static loadPlyr() {
    if (window.Plyr) return Promise.resolve(window.Plyr);
    if (VideoCard.plyrLoadPromise) return VideoCard.plyrLoadPromise;

    VideoCard.plyrLoadPromise = new Promise((resolve, reject) => {
      if (!document.querySelector('link[data-plyr-styles]')) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.jsdelivr.net/npm/plyr@3.7.8/dist/plyr.css';
        link.setAttribute('data-plyr-styles', '');
        document.head.appendChild(link);
      }

      const existingScript =
        document.querySelector('script[data-plyr-script]') ||
        document.querySelector('script[src*="plyr"]');

      const resolveWhenReady = () => {
        const startedAt = Date.now();
        const check = () => {
          if (window.Plyr) {
            resolve(window.Plyr);
            return;
          }

          if (Date.now() - startedAt > 5000) {
            reject(new Error('Plyr failed to load'));
            return;
          }

          window.setTimeout(check, 50);
        };

        check();
      };

      if (existingScript) {
        resolveWhenReady();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/plyr@3.7.8/dist/plyr.polyfilled.min.js';
      script.defer = true;
      script.setAttribute('data-plyr-script', '');
      script.onload = resolveWhenReady;
      script.onerror = () => reject(new Error('Plyr failed to load'));
      document.head.appendChild(script);
    });

    return VideoCard.plyrLoadPromise;
  }

  handleKeyup(event) {
    if (event.code === 'Escape') this.closeModal();
  }

  async handleTriggerClick(event) {
    const provider = this.dataset.videoProvider;
    const videoId = this.dataset.videoId;
    if (!provider || !videoId || !this.modal || !this.playerRoot) return;

    try {
      await this.openModal(provider, videoId, event.currentTarget);
    } catch (error) {
      console.error(error);
    }
  }

  async openModal(provider, videoId, opener) {
    const Plyr = await VideoCard.loadPlyr();

    this.lastOpener = opener;
    this.closeModal(false);

    const playerEl = document.createElement('div');
    playerEl.setAttribute('data-plyr-provider', provider);
    playerEl.setAttribute('data-plyr-embed-id', videoId);
    this.playerRoot.appendChild(playerEl);

    this.plyr = new Plyr(playerEl, {
      autoplay: true,
      controls: ['play', 'progress', 'current-time', 'mute', 'fullscreen'],
      youtube: {
        rel: 0,
        modestbranding: 1,
        iv_load_policy: 3,
        playsinline: 1,
        origin: window.location.origin,
      },
      vimeo: {
        byline: false,
        portrait: false,
        title: false,
      },
    });

    this.modal.hidden = false;
    document.body.classList.add('overflow-hidden');
    document.addEventListener('keyup', this.boundKeyup);
    this.dialog?.focus();

    this.plyr.on('ready', () => {
      this.plyr.play();
    });
  }

  closeModal(restoreFocus = true) {
    if (this.plyr) {
      this.plyr.destroy();
      this.plyr = null;
    }

    if (this.playerRoot) {
      this.playerRoot.innerHTML = '';
    }

    if (this.modal) {
      this.modal.hidden = true;
    }

    document.body.classList.remove('overflow-hidden');
    document.removeEventListener('keyup', this.boundKeyup);

    if (restoreFocus && this.lastOpener) {
      this.lastOpener.focus();
      this.lastOpener = null;
    }
  }
}

if (!customElements.get('video-card')) {
  customElements.define('video-card', VideoCard);
}

class VideoCards extends HTMLElement {
  connectedCallback() {
    this.initSwiper();
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  initSwiper() {
    const swiperElement = this.querySelector('.swiper');
    if (!swiperElement || this.swiper) return;

    const slidesMobile = Number(this.dataset.slidesMobile) || 1.7;
    const slidesDesktop = Number(this.dataset.slidesDesktop) || 3;
    const spaceMobile = Number(this.dataset.spaceMobile) || 8;
    const spaceDesktop = Number(this.dataset.spaceDesktop) || 12;

    this.swiper = new Swiper(swiperElement, {
      slidesPerView: slidesMobile,
      spaceBetween: spaceMobile,
      breakpoints: {
        990: {
          slidesPerView: slidesDesktop,
          spaceBetween: spaceDesktop,
        },
      },
    });
  }
}

if (!customElements.get('video-cards')) {
  customElements.define('video-cards', VideoCards);
}

document.addEventListener('DOMContentLoaded', function() {
  customElements.define('content-sidebar', class extends HTMLElement {
      constructor() {
          super();

          this.mainHs = this.closest('.article-template__content').querySelectorAll('h1,h2');
          this.secondaryHs = this.closest('.article-template__content').querySelectorAll('h3,h4,h5,h6');
          var hIndex = 0
          
          this.mainHs.forEach((h) => {
            if(h.textContent && h.textContent != ""){
              const hId = h.textContent.toLowerCase().replace(/[^a-z0-9 ]/g, '').replace(/ /g, '-');
              const sidebarItem = document.createElement('a');
              sidebarItem.classList.add('content-sidebar__item');
              sidebarItem.classList.add('text-s-regular');
              sidebarItem.href = `#${hId}`;
              sidebarItem.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9.93477 7.30953L5.55977 11.6845C5.51913 11.7252 5.47087 11.7574 5.41776 11.7794C5.36465 11.8014 5.30773 11.8127 5.25024 11.8127C5.19276 11.8127 5.13584 11.8014 5.08273 11.7794C5.02962 11.7574 4.98136 11.7252 4.94071 11.6845C4.90006 11.6439 4.86782 11.5956 4.84582 11.5425C4.82382 11.4894 4.8125 11.4325 4.8125 11.375C4.8125 11.3175 4.82382 11.2606 4.84582 11.2075C4.86782 11.1544 4.90006 11.1061 4.94071 11.0655L9.00673 7L4.94071 2.93453C4.85862 2.85244 4.8125 2.7411 4.8125 2.625C4.8125 2.5089 4.85862 2.39756 4.94071 2.31547C5.0228 2.23338 5.13415 2.18726 5.25024 2.18726C5.36634 2.18726 5.47768 2.23338 5.55977 2.31547L9.93477 6.69047C9.97545 6.7311 10.0077 6.77935 10.0297 6.83246C10.0518 6.88557 10.0631 6.9425 10.0631 7C10.0631 7.05749 10.0518 7.11442 10.0297 7.16754C10.0077 7.22065 9.97545 7.2689 9.93477 7.30953Z" fill="#666666"/>
                                      </svg>
                                      <span class="content-sidebar__item-text">${h.textContent}</span>`;
              this.querySelector('.content-sidebar__inner').appendChild(sidebarItem);
              h.setAttribute("id", `${hId}`);
              hIndex++
            }
          });
      }
  });
});


class AboutBanner extends HTMLElement {
  connectedCallback() {
    this.initGSAP();
  }

  disconnectedCallback() {
    this._ctx?.revert();
  }

  initGSAP() {
    if (
      typeof gsap === 'undefined' ||
      typeof ScrollTrigger === 'undefined' ||
      typeof SplitText === 'undefined'
    ) {
      setTimeout(() => this.initGSAP(), 100);
      return;
    }

    gsap.registerPlugin(ScrollTrigger, SplitText);

    this._ctx = gsap.context(() => {
      const blurAmount = window.innerWidth > 989 ? 12 : 8;

      this.querySelectorAll('.text-reveal').forEach((el) => {
        const textEls = el.querySelectorAll('.about-banner__text');
        if (!textEls.length) return;

        const wrapper = el.closest('.about-banner__item-wrapper');
        const split = new SplitText(textEls, {
          type: 'chars,words,lines',
          linesClass: 'text-reveal__wrapper',
          wordsClass: 'text-reveal__word',
          charsClass: 'text-reveal__char',
        });

        const charCount = split.chars.length;
        const revealDuration = 1;
        const holdDuration = 3;
        const getPhaseScroll = () =>
          Math.max(window.innerHeight * 0.6, charCount * 8);

        wrapper.style.setProperty('--phase-scroll', `${getPhaseScroll()}px`);

        gsap.timeline({
          scrollTrigger: {
            trigger: wrapper || el,
            start: 'top top',
            end: () => {
              const scroll = getPhaseScroll();
              wrapper.style.setProperty('--phase-scroll', `${scroll}px`);
              return `+=${scroll}`;
            },
            pin: true,
            scrub: true,
            anticipatePin: 1,
          },
        })
          .fromTo(
            split.chars,
            { filter: `blur(${blurAmount}px)`, opacity: 0 },
            {
              filter: 'blur(0px)',
              opacity: 1,
              ease: 'none',
              stagger: 0.04,
              duration: revealDuration,
            }
          )
          .to({}, { duration: holdDuration })
          .to(split.chars, {
            filter: `blur(${blurAmount}px)`,
            opacity: 0,
            ease: 'none',
            stagger: { each: 0.04, from: 'start' },
            duration: revealDuration,
          });
      });

      ScrollTrigger.refresh();
    }, this);
  }

}

customElements.define("about-banner", AboutBanner);


class IngredientsSpotlight extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('.ingredients-spotlight__swiper');
    this.wrapper = this.slider?.querySelector('.swiper-wrapper');
    this.mobileGrid = this.querySelector('.ingredients-spotlight__right-inner');
    this.slideStash = document.createDocumentFragment();
    this.activeTab = '';
    this.desktopQuery = window.matchMedia('(min-width: 990px)');

    this.boundClick = this.handleClick.bind(this);
    this.boundDesktopChange = this.handleDesktopChange.bind(this);

    this.addEventListener('click', this.boundClick);
    this.desktopQuery.addEventListener('change', this.boundDesktopChange);

    if (this.desktopQuery.matches) {
      this.initSwiper();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.boundClick);
    this.desktopQuery?.removeEventListener('change', this.boundDesktopChange);
    this.destroySwiper();
  }

  get viewMoreBtn() {
    return this.querySelector('.ingredients-spotlight__view-more-btn');
  }

  handleClick(event) {
    const tabBtn = event.target.closest('.ingredients-spotlight__tab');
    if (tabBtn && this.contains(tabBtn)) {
      this.handleTabClick(tabBtn);
      return;
    }

    const viewMoreBtn = event.target.closest('.ingredients-spotlight__view-more-btn');
    if (viewMoreBtn && this.contains(viewMoreBtn)) {
      event.preventDefault();
      this.handleViewMoreClick(viewMoreBtn);
    }
  }

  handleDesktopChange(event) {
    if (event.matches) {
      this.filterDesktopSlides(this.activeTab);
      this.initSwiper();
      return;
    }
    this.destroySwiper();
  }

  handleTabClick(btn) {
    const tab = btn.dataset.tab || '';
    if (tab === this.activeTab) return;

    this.activeTab = tab;
    this.setActiveTab(tab);
    this.filterByTab(tab);
  }

  handleViewMoreClick(btn) {
    // Only toggle overflow while the "All" tab is active
    if (this.activeTab) return;

    const expanded = btn.getAttribute('data-expanded') === 'true';
    const nextExpanded = !expanded;
    const labelMore = btn.getAttribute('data-label-more') || '';
    const labelLess = btn.getAttribute('data-label-less') || '';

    btn.setAttribute('data-expanded', String(nextExpanded));
    btn.setAttribute('aria-expanded', String(nextExpanded));
    btn.textContent = nextExpanded ? labelLess : labelMore;

    this.filterMobileItems('');
  }

  setActiveTab(tab) {
    this.querySelectorAll('.ingredients-spotlight__tab').forEach((btn) => {
      btn.classList.toggle('active', (btn.dataset.tab || '') === tab);
    });
  }

  filterByTab(tab) {
    this.filterMobileItems(tab);
    this.filterDesktopSlides(tab);

    const viewMoreBtn = this.viewMoreBtn;
    if (viewMoreBtn) {
      // View more only applies to the unfiltered mobile list
      viewMoreBtn.classList.toggle('hidden', Boolean(tab));
    }

    if (this.desktopQuery.matches) {
      this._lastSlideHeight = undefined;
      this._lastContainerHeight = undefined;
      this.destroySwiper();
      this.initSwiper();
    }
  }

  filterMobileItems(tab) {
    if (!this.mobileGrid) return;

    const expanded = this.viewMoreBtn?.getAttribute('data-expanded') === 'true';

    this.mobileGrid.querySelectorAll('.ingredients-spotlight__ingredient').forEach((item) => {
      const type = item.dataset.type || '';
      const isOverflow = item.getAttribute('data-default-hidden') === 'true';
      let shouldHide;

      if (!tab) {
        shouldHide = isOverflow && !expanded;
      } else {
        shouldHide = type !== tab;
      }

      item.classList.toggle('hidden', shouldHide);
    });
  }

  filterDesktopSlides(tab) {
    if (!this.wrapper) return;

    // Restore every slide into the wrapper first
    while (this.slideStash.firstChild) {
      this.wrapper.appendChild(this.slideStash.firstChild);
    }

    [...this.wrapper.querySelectorAll('.swiper-slide')].forEach((slide) => {
      const type = slide.dataset.type || '';
      const show = !tab || type === tab;
      if (!show) this.slideStash.appendChild(slide);
    });
  }

  destroySwiper() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }

  initSwiper() {
    if (!this.slider || this.swiperInstance || !this.desktopQuery.matches) return;

    const visibleSlides = this.wrapper?.querySelectorAll('.swiper-slide') || [];
    const controls = this.querySelector('.ingredients-spotlight__controls');
    if (controls) {
      controls.classList.toggle('--disabled', visibleSlides.length === 0);
    }
    if (!visibleSlides.length) return;

    const spaceBetween = 16;

    this.swiperInstance = new Swiper(this.slider, {
      slidesPerView: 5,
      slidesPerGroup: 5,
      spaceBetween,
      grid: {
        rows: 2,
        fill: 'row',
      },
      breakpoints: {
        1200: {
          slidesPerView: 6,
          slidesPerGroup: 6,
          spaceBetween: 24,
          grid: {
            rows: 2,
            fill: 'row',
          },
        },
      },
      navigation: {
        nextEl: this.querySelector('.ingredients-spotlight__next'),
        prevEl: this.querySelector('.ingredients-spotlight__prev'),
      },
      on: {
        init: () => {
          this.updateGridHeight();
          this.waitForImages().then(() => this.updateGridHeight());
        },
        breakpoint: () => {
          requestAnimationFrame(() => this.updateGridHeight());
        },
        resize: () => {
          this.updateGridHeight();
        },
      },
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.updateGridHeight();
    });
    this.resizeObserver.observe(this.slider);
  }

  waitForImages() {
    const images = [...this.slider.querySelectorAll('img')];
    if (!images.length) return Promise.resolve();

    return Promise.all(
      images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      })
    );
  }

  updateGridHeight() {
    if (!this.slider || !this.swiperInstance || this._updatingHeight) return;

    this._updatingHeight = true;

    const rows = this.swiperInstance.params.grid?.rows || 2;
    const spaceBetween = this.swiperInstance.params.spaceBetween || 0;
    const slides = [...this.slider.querySelectorAll('.swiper-slide')];

    if (!slides.length) {
      this._updatingHeight = false;
      return;
    }

    // Let slides size to their content so we can measure natural height
    this.slider.style.height = 'auto';
    slides.forEach((slide) => {
      slide.style.height = 'auto';
    });

    // Swiper Grid forces equal slide heights, so use the tallest item overall
    let maxSlideHeight = 0;
    slides.forEach((slide) => {
      maxSlideHeight = Math.max(maxSlideHeight, slide.scrollHeight);
    });

    if (!maxSlideHeight) {
      this._updatingHeight = false;
      return;
    }

    const containerHeight = maxSlideHeight * rows + spaceBetween * (rows - 1);

    // Skip write if nothing changed (avoids ResizeObserver loops)
    if (
      this._lastSlideHeight === maxSlideHeight &&
      this._lastContainerHeight === containerHeight
    ) {
      this._updatingHeight = false;
      return;
    }

    this._lastSlideHeight = maxSlideHeight;
    this._lastContainerHeight = containerHeight;

    this.slider.style.height = `${containerHeight}px`;
    slides.forEach((slide) => {
      slide.style.height = `${maxSlideHeight}px`;
    });

    this.swiperInstance.updateSize();
    this.swiperInstance.updateSlides();

    requestAnimationFrame(() => {
      this._updatingHeight = false;
    });
  }
}

if (!customElements.get('ingredients-spotlight')) {
  customElements.define('ingredients-spotlight', IngredientsSpotlight);
}

class EventsSlider extends HTMLElement {
  connectedCallback() {
    this.panels = Array.from(this.querySelectorAll('.events-slider__panel'));
    this.toggles = Array.from(this.querySelectorAll('.events-slider__toggle'));
    this.swipers = new Map();
    this.activeType = this.dataset.defaultType || 'future';
    this.boundToggleClick = this.handleToggleClick.bind(this);

    this.toggles.forEach((toggle) => {
      toggle.addEventListener('click', this.boundToggleClick);
    });

    this.initSwipers();
    this.showPanel(this.activeType);
  }

  disconnectedCallback() {
    this.toggles.forEach((toggle) => {
      toggle.removeEventListener('click', this.boundToggleClick);
    });

    this.swipers.forEach((swiper) => {
      swiper.destroy(true, true);
    });
    this.swipers.clear();
  }

  handleToggleClick(event) {
    const toggle = event.currentTarget;
    const type = toggle.dataset.type;
    if (!type || type === this.activeType) return;

    this.activeType = type;
    this.showPanel(type);

    this.toggles.forEach((btn) => {
      const isActive = btn.dataset.type === type;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    });

    const activeSwiper = this.swipers.get(type);
    if (activeSwiper) {
      activeSwiper.update();
    }
  }

  showPanel(type) {
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.type === type;
      panel.classList.toggle('is-hidden', !isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  initSwipers() {
    if (typeof Swiper === 'undefined') return;

    const slidesMobile = Number(this.dataset.slidesMobile) || 1.2;
    const slidesDesktop = Number(this.dataset.slidesDesktop) || 3;
    const spaceMobile = Number(this.dataset.spaceMobile) || 12;
    const spaceDesktop = Number(this.dataset.spaceDesktop) || 16;

    this.panels.forEach((panel) => {
      const type = panel.dataset.type;
      const swiperElement = panel.querySelector('.swiper');
      if (!type || !swiperElement || this.swipers.has(type)) return;

      const swiper = new Swiper(swiperElement, {
        slidesPerView: slidesMobile,
        spaceBetween: spaceMobile,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: panel.querySelector('.events-slider__next'),
          prevEl: panel.querySelector('.events-slider__prev'),
        },
        breakpoints: {
          990: {
            slidesPerView: slidesDesktop,
            spaceBetween: spaceDesktop,
          },
        },
      });

      this.swipers.set(type, swiper);
    });
  }
}

if (!customElements.get('events-slider')) {
  customElements.define('events-slider', EventsSlider);
}

class ExpertsSlider extends HTMLElement {
  connectedCallback() {
    this.initSwiper();
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  updateCenteredState() {
    if (!this.swiper) return;
    this.classList.toggle('is-centered', this.swiper.isLocked);
  }

  initSwiper() {
    if (typeof Swiper === 'undefined') return;

    const spaceBetween = Number(this.dataset.spaceBetween) || 16;

    this.swiper = new Swiper(this, {
      slidesPerView: 'auto',
      spaceBetween,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      on: {
        init: () => this.updateCenteredState(),
        resize: () => this.updateCenteredState(),
        lock: () => this.updateCenteredState(),
        unlock: () => this.updateCenteredState(),
        observerUpdate: () => this.updateCenteredState(),
      },
    });
  }
}

if (!customElements.get('experts-slider')) {
  customElements.define('experts-slider', ExpertsSlider);
}

class ConfirmationPopup extends HTMLElement {
  connectedCallback() {
    this.storageKey = this.dataset.storageKey || '';
    this.exitUrl = this.dataset.exitUrl || '/';
    this.sectionId = this.dataset.sectionId || '';
    this.isDesignMode = this.dataset.designMode === 'true';
    this.sectionEl = this.closest('.shopify-section') || this;

    if (!this.isDesignMode) {
      try {
        if (this.storageKey && sessionStorage.getItem(this.storageKey) === '1') {
          this.sectionEl.remove();
          return;
        }
      } catch (e) {
        // sessionStorage may be unavailable
      }
    }

    this.keepBtn = this.querySelector('[data-confirmation-popup-action="keep"]');
    this.exitBtn = this.querySelector('[data-confirmation-popup-action="exit"]');

    this.onKeep = this.onKeep.bind(this);
    this.onExit = this.onExit.bind(this);
    this.onSectionSelect = this.onSectionSelect.bind(this);
    this.onSectionDeselect = this.onSectionDeselect.bind(this);

    this.keepBtn?.addEventListener('click', this.onKeep);
    this.exitBtn?.addEventListener('click', this.onExit);

    if (this.isDesignMode) {
      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
      return;
    }

    this.open();
  }

  disconnectedCallback() {
    this.keepBtn?.removeEventListener('click', this.onKeep);
    this.exitBtn?.removeEventListener('click', this.onExit);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    this.unlockScroll();
  }

  onSectionSelect(event) {
    if (event.detail?.sectionId !== this.sectionId) return;
    this.open();
  }

  onSectionDeselect(event) {
    if (event.detail?.sectionId !== this.sectionId) return;
    this.hide();
  }

  open() {
    this.classList.add('confirmation-popup--active');
    this.lockScroll();
  }

  hide() {
    this.classList.remove('confirmation-popup--active');
    this.unlockScroll();
  }

  close() {
    this.hide();

    if (this.isDesignMode) return;

    try {
      if (this.storageKey) {
        sessionStorage.setItem(this.storageKey, '1');
      }
    } catch (e) {
      // sessionStorage may be unavailable
    }

    setTimeout(() => {
      this.sectionEl?.remove();
    }, 250);
  }

  onKeep(event) {
    event.preventDefault();
    this.close();
  }

  onExit(event) {
    event.preventDefault();
    if (this.isDesignMode) {
      this.hide();
      return;
    }
    window.location.href = this.exitUrl;
  }

  lockScroll() {
    document.documentElement.classList.add('confirmation-popup-no-scroll');
    document.body.classList.add('confirmation-popup-no-scroll');
  }

  unlockScroll() {
    document.documentElement.classList.remove('confirmation-popup-no-scroll');
    document.body.classList.remove('confirmation-popup-no-scroll');
  }
}

if (!customElements.get('confirmation-popup')) {
  customElements.define('confirmation-popup', ConfirmationPopup);
}


class ProductRatingSummary extends HTMLElement {
  constructor() {
    super();
    this.boundPillClick = this.handlePillClick.bind(this);
  }

  connectedCallback() {
    this.linkUrl = (this.dataset.linkUrl || '').trim();
    this.targetClass = (this.dataset.scrollTargetClass || 'trustpilot-widget').trim();

    if (this.linkUrl) return;

    this.pill = this.querySelector('.product-rating-summary__pill');
    this.pill?.addEventListener('click', this.boundPillClick);
  }

  disconnectedCallback() {
    this.pill?.removeEventListener('click', this.boundPillClick);
  }

  handlePillClick(event) {
    const target = document.querySelector(`.${this.targetClass}`);
    if (!target) return;

    event.preventDefault();

    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    const scrollTop = Math.max(0, targetTop - window.innerHeight / 2);

    window.scrollTo({
      top: scrollTop,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('product-rating-summary')) {
  customElements.define('product-rating-summary', ProductRatingSummary);
}


class ComplexFAQs extends HTMLElement {
  connectedCallback() {
    this.togglers = Array.from(this.querySelectorAll('.faqs__toggler'));
    this.lists = Array.from(this.querySelectorAll('.faqs__list'));
    this.boundTogglerClick = this.handleTogglerClick.bind(this);

    this.togglers.forEach((toggler) => {
      toggler.addEventListener('click', this.boundTogglerClick);
    });
  }

  disconnectedCallback() {
    this.togglers.forEach((toggler) => {
      toggler.removeEventListener('click', this.boundTogglerClick);
    });
  }

  handleTogglerClick(event) {
    const toggler = event.currentTarget;
    const type = toggler.dataset.faqType;
    console.log(type);
    this.lists.forEach((list) => {
      list.classList.toggle('hidden', list.dataset.faqType !== type);
    });
  }
}

if (!customElements.get('complex-faqs')) {
  customElements.define('complex-faqs', ComplexFAQs);
}

class PressMarquee extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.press-marquee__viewport');
    this.track = this.querySelector('.press-marquee__track');
    this.sourceGroup = this.querySelector('.press-marquee__group');
    if (!this.viewport || !this.track || !this.sourceGroup) return;

    this.boundFill = this.fillTrack.bind(this);
    this.fillTrack();

    this.resizeObserver = new ResizeObserver(this.boundFill);
    this.resizeObserver.observe(this.viewport);
    this.resizeObserver.observe(this.sourceGroup);

    this.sourceGroup.querySelectorAll('img').forEach((img) => {
      if (!img.complete) {
        img.addEventListener('load', this.boundFill, { once: true });
        img.addEventListener('error', this.boundFill, { once: true });
      }
    });
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  fillTrack() {
    const groupWidth = this.sourceGroup.getBoundingClientRect().width;
    const viewportWidth = this.viewport.getBoundingClientRect().width;
    if (!groupWidth || !viewportWidth) return;

    const needed = Math.max(2, Math.ceil(viewportWidth / groupWidth) + 1);
    const groups = this.track.querySelectorAll('.press-marquee__group');

    for (let i = groups.length; i < needed; i++) {
      const clone = this.sourceGroup.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
        node.removeAttribute('data-shopify-editor-block');
      });
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      this.track.appendChild(clone);
    }

    const shift = `-${groupWidth}px`;
    if (this.track.style.getPropertyValue('--press-shift') !== shift) {
      this.track.style.setProperty('--press-shift', shift);
    }
  }
}

if (!customElements.get('press-marquee')) {
  customElements.define('press-marquee', PressMarquee);
}


class CartSubscriptionUpsell extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.cart-item-upsell__button, .cart-night-upsell__button');
    this.onClick = this.handleClick.bind(this);
    this.button?.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  async handleClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const currentKey = this.button.dataset.currentKey;
    const quantity = parseInt(this.button.dataset.currentQuantity || '1', 10);
    const subscriptionId = this.button.dataset.subscriptionId;
    const sellingPlanId = this.button.dataset.subscriptionSellingPlan;
    const giftId = this.button.dataset.giftId;
    const giftProperties = this.button.dataset.giftProperties;
    const gwpIds = this.button.dataset.gwpIds ? this.button.dataset.gwpIds.split(',').filter(Boolean) : [];

    try {
      if (currentKey) {
        await this.removeCurrentItem(currentKey);
      }
      const addResponse = await this.addSubscriptionItem(
        subscriptionId,
        quantity,
        sellingPlanId,
        giftId,
        giftProperties,
        gwpIds
      );

      if (cart && 'renderContents' in cart) {
        cart.renderContents(addResponse);
      }
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  }

  async removeCurrentItem(key) {
    return fetch(`${routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: key,
        quantity: 0,
      }),
    }).then((res) => res.json());
  }

  async addSubscriptionItem(id, quantity, sellingPlan, giftId, giftProperties, gwpIds) {
    const items = [
      {
        id: id,
        quantity: quantity || 1,
      },
    ];
    if (sellingPlan) {
      items[0].selling_plan = sellingPlan;
    }
    if (giftId && giftId !== '' && giftId.split(',').length > 0) {
      giftId.split(',').forEach((_id) => {
        items.push({
          id: _id,
          quantity: quantity,
          parent_id: id,
          properties: JSON.parse(giftProperties),
        });
      });
    }
    // Regalos (GWP) del upsell a suscripción: se añaden con la propiedad _gwp
    if (gwpIds && gwpIds.length > 0) {
      gwpIds.forEach((_id) => {
        items.push({
          id: _id,
          quantity: quantity,
          parent_id: id,
          properties: {
            _gwp: true,
          },
        });
      });
    }

    const cartDrawerId = document.querySelector('.cart-drawer')?.dataset.sectionId || 'cart-drawer';
    return fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: `${cartDrawerId},cart-icon-bubble`,
      }),
    }).then((res) => res.json());
  }
}

if (!customElements.get('cart-subscription-upsell')) {
  customElements.define('cart-subscription-upsell', CartSubscriptionUpsell);
}

class BfGrid extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('.bf-grid__modal');
    this.swiperEl = this.querySelector('.bf-grid__modal-swiper');
    this.cardsRoot = this.querySelector('.bf-grid__cards');
    this.currentNodes = this.querySelectorAll('[data-bf-current]');
    this.totalNodes = this.querySelectorAll('[data-bf-total]');
    this.mq = window.matchMedia('(max-width: 749px)');

    this.onOpenClick = this.onOpenClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);

    this.querySelectorAll('[data-bf-open]').forEach((btn) => {
      btn.addEventListener('click', this.onOpenClick);
    });
    this.querySelectorAll('[data-bf-close]').forEach((el) => {
      el.addEventListener('click', this.onCloseClick);
    });
    document.addEventListener('keydown', this.onKeyDown);

    this.sortCards();
    if (this.mq.addEventListener) {
      this.mq.addEventListener('change', this.onBreakpointChange);
    } else {
      this.mq.addListener(this.onBreakpointChange);
    }

    this.initSwiper();
  }

  disconnectedCallback() {
    this.querySelectorAll('[data-bf-open]').forEach((btn) => {
      btn.removeEventListener('click', this.onOpenClick);
    });
    this.querySelectorAll('[data-bf-close]').forEach((el) => {
      el.removeEventListener('click', this.onCloseClick);
    });
    document.removeEventListener('keydown', this.onKeyDown);

    if (this.mq) {
      if (this.mq.removeEventListener) {
        this.mq.removeEventListener('change', this.onBreakpointChange);
      } else {
        this.mq.removeListener(this.onBreakpointChange);
      }
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.closeModal();
  }

  onBreakpointChange() {
    this.sortCards();
  }

  sortCards() {
    if (!this.cardsRoot) return;

    const cards = Array.from(this.cardsRoot.querySelectorAll('.bf-grid__card'));
    const key = this.mq.matches ? 'ordM' : 'ordD';

    const sorted = cards
      .map((el, idx) => ({
        el,
        ordM: parseInt(el.getAttribute('data-ord-m') || '999', 10),
        ordD: parseInt(el.getAttribute('data-ord-d') || '999', 10),
        idx,
      }))
      .sort((a, b) => {
        if (a[key] !== b[key]) return a[key] - b[key];
        return a.idx - b.idx;
      });

    const frag = document.createDocumentFragment();
    sorted.forEach((item) => frag.appendChild(item.el));
    this.cardsRoot.appendChild(frag);
  }

  initSwiper() {
    if (typeof Swiper === 'undefined' || !this.swiperEl || this.swiper) return;

    const prevEl = this.querySelector('.bf-grid__nav--prev');
    const nextEl = this.querySelector('.bf-grid__nav--next');
    this.slideCount = this.swiperEl.querySelectorAll('.bf-grid__slide').length;

    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: true,
      speed: 400,
      allowTouchMove: true,
      navigation: {
        prevEl,
        nextEl,
      },
      on: {
        slideChange: () => this.updatePager(),
        init: () => this.updatePager(),
      },
    });
  }

  updatePager() {
    if (!this.swiper) return;
    const current = (this.swiper.realIndex || 0) + 1;
    const total = this.slideCount || 1;

    this.currentNodes.forEach((node) => {
      node.textContent = String(current);
    });
    this.totalNodes.forEach((node) => {
      node.textContent = String(total);
    });
  }

  onOpenClick(event) {
    const btn = event.currentTarget;
    const index = Number(btn.dataset.bfOpen);
    if (Number.isNaN(index)) return;
    this.openModal(index);
  }

  onCloseClick() {
    this.closeModal();
  }

  onKeyDown(event) {
    if (!this.modal || this.modal.hidden) return;
    if (event.key === 'Escape') this.closeModal();
    if (event.key === 'ArrowLeft') this.swiper?.slidePrev();
    if (event.key === 'ArrowRight') this.swiper?.slideNext();
  }

  openModal(index) {
    if (!this.modal) return;
    this.modal.hidden = false;
    document.documentElement.classList.add('bf-grid-modal-open');

    if (!this.swiper) this.initSwiper();
    if (this.swiper) {
      this.swiper.slideToLoop(index, 0);
      this.swiper.update();
      this.updatePager();
    }
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.hidden = true;
    document.documentElement.classList.remove('bf-grid-modal-open');
  }
}

if (!customElements.get('bf-grid')) {
  customElements.define('bf-grid', BfGrid);
}